'use client';

import { FormEvent, useId, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import ContactService from '@/app/services/send-message';

type Status = 'idle' | 'loading' | 'success' | 'error';

type NotifyFormProps = {
  /** "dark" for dark/photo backgrounds, "light" for cream/white backgrounds */
  variant?: 'dark' | 'light';
  /** Show the email field straight away instead of the button first */
  startOpen?: boolean;
  /** Classes for the initial "Notify me" button so it matches each section */
  buttonClassName?: string;
  className?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NotifyForm({
  variant = 'light',
  startOpen = false,
  buttonClassName = 'bg-[#FFE37B] hover:bg-[#FACC15] text-[#064E3B] font-bold py-4 px-8 rounded-full text-lg transition-all cursor-pointer',
  className = '',
}: NotifyFormProps) {
  const t = useTranslations('Home');
  const locale = useLocale();
  const inputId = useId();
  const [isOpen, setIsOpen] = useState(startOpen);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorKey, setErrorKey] = useState<'invalid_email' | 'subscribe_error'>('subscribe_error');

  const messageColor = variant === 'dark' ? 'text-[#FFE37B]' : 'text-[#B91C1C]';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!EMAIL_REGEX.test(email.trim())) {
      setErrorKey('invalid_email');
      setStatus('error');
      return;
    }

    setStatus('loading');
    try {
      const res: any = await ContactService.subscribeMail({ email, locale });
      if (!res?.data?.success) throw new Error('subscribe failed');
      setStatus('success');
    } catch {
      setErrorKey('subscribe_error');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className={`flex ${className}`}>
        <div
          role="status"
          className="inline-flex items-center gap-3 bg-white text-[#064E3B] font-bold py-3 pl-3 pr-6 rounded-full shadow-lg"
        >
          <span className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center shrink-0">
            <Check size={18} strokeWidth={3} />
          </span>
          {t('subscribe_success')}
        </div>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className={`flex ${className}`}>
        <button type="button" onClick={() => setIsOpen(true)} className={buttonClassName}>
          {t('notify_launch')}
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="flex items-center w-full max-w-md bg-white rounded-full p-1.5 pl-6 shadow-lg border-2 border-[#FFE37B] focus-within:border-[#FACC15]"
      >
        <label htmlFor={inputId} className="sr-only">
          {t('email_placeholder')}
        </label>
        <input
          id={inputId}
          type="email"
          name="email"
          required
          autoFocus={!startOpen}
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === 'error') setStatus('idle');
          }}
          placeholder={t('email_placeholder')}
          className="flex-1 min-w-0 bg-transparent outline-none text-[#064E3B] text-base placeholder:text-[#064E3B]/50 py-2"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          aria-label={t('subscribe')}
          className="shrink-0 flex items-center gap-2 bg-[#FFE37B] hover:bg-[#FACC15] text-[#064E3B] font-bold py-3 px-4 sm:px-6 rounded-full transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'loading' ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              <span className="hidden sm:inline">{t('subscribe')}</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>
      {status === 'error' ? (
        <p role="alert" className={`${messageColor} text-sm font-semibold mt-2 ml-4`}>
          {t(errorKey)}
        </p>
      ) : null}
    </div>
  );
}
